import React, { useState, Component } from 'react';
import { BuildingIcon, UserIcon, SearchIcon, Loader2Icon, CheckCircle2Icon, LandmarkIcon, SmartphoneIcon } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { THAI_PROVINCES, THAI_BANKS } from '../utils/mockSuppliers';
export function SupplierSelfService() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [supplierType, setSupplierType] = useState<'company' | 'individual'>('company');
  // DBD Lookup State
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [dbdMeta, setDbdMeta] = useState<{
    registerDate?: string;
    juristicStatus?: string;
    juristicType?: string;
    isMockFallback?: boolean;
  }>({});
  const [formData, setFormData] = useState({
    // Shared
    email: '',
    phone: '',
    useAsBillingEmail: true,
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    country: 'Thailand',
    postcode: '',
    // Company
    taxId: '',
    companyNameTh: '',
    companyNameEn: '',
    branch: '00000',
    vatRegistered: false,
    contactPerson: '',
    // Individual
    idType: 'citizen_id',
    idNumber: '',
    fullNameTh: '',
    fullNameEn: '',
    individualTaxId: '',
    // Payout method
    payoutMethod: 'bank_account',
    bankName: '',
    accountNumber: '',
    accountName: '',
    promptPayType: 'phone',
    promptPayId: '',
    promptPayName: ''
  });
  const handleLookup = async () => {
    if (!formData.taxId || formData.taxId.length !== 13) {
      setLookupError('Please enter a valid 13-digit Tax ID / Registration Number');
      setLookupSuccess(false);
      return;
    }
    setLookupError('');
    setIsLookingUp(true);
    setLookupSuccess(false);
    setDbdMeta({});
    const DBD_API_BASE = 'https://opendata.dbd.go.th/api/action/datastore_search';
    const resourceId = 'f008dbbf-ddfa-4e3a-bac4-358a1a2b9853';
    const apiUrl = `${DBD_API_BASE}?resource_id=${resourceId}&filters=${encodeURIComponent(JSON.stringify({
      juristicID: formData.taxId
    }))}&limit=1`;
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) throw new Error(`API returned status ${response.status}`);
      const data = await response.json();
      if (!data.success || !data.result?.records?.length) {
        setIsLookingUp(false);
        setLookupError(`No company found for Tax ID ${formData.taxId}. Please verify the number and try again.`);
        return;
      }
      const record = data.result.records[0];
      const companyNameLocal = record.juristicNameTH || record.juristicNameTh || record.companyNameTH || '';
      const companyNameEn = record.juristicNameEN || record.juristicNameEn || record.companyNameEN || '';
      const addressFull = record.addressDesc || record.address || record.juristicAddress || '';
      const registerDate = record.registerDate || record.registrationDate || '';
      const juristicStatus = record.juristicStatus || record.status || '';
      const juristicType = record.juristicType || record.entityType || '';
      let addressLine1 = addressFull;
      let addressLine2 = '';
      let city = '';
      let postcode = '';
      const postcodeMatch = addressFull.match(/\b(\d{5})\b\s*$/);
      if (postcodeMatch) {
        postcode = postcodeMatch[1];
        addressLine1 = addressFull.replace(postcodeMatch[0], '').trim();
      }
      const parts = addressLine1.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
      if (parts.length >= 3) {
        addressLine1 = parts.slice(0, Math.ceil(parts.length / 2)).join(', ');
        const remaining = parts.slice(Math.ceil(parts.length / 2));
        city = remaining.pop() || '';
        addressLine2 = remaining.join(', ');
      } else if (parts.length === 2) {
        addressLine1 = parts[0];
        city = parts[1];
      }
      setFormData(prev => ({
        ...prev,
        companyNameTh: companyNameLocal || prev.companyNameTh,
        companyNameEn: companyNameEn || prev.companyNameEn,
        addressLine1: addressLine1 || prev.addressLine1,
        addressLine2: addressLine2 || prev.addressLine2,
        city: city || prev.city,
        postcode: postcode || prev.postcode
      }));
      setDbdMeta({
        registerDate,
        juristicStatus,
        juristicType,
        isMockFallback: false
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    } catch (err: any) {
      console.warn('DBD API call failed, using mock fallback:', err.message);
      setFormData(prev => ({
        ...prev,
        companyNameTh: 'Test Company Limited',
        companyNameEn: 'Test Company Limited',
        addressLine1: '123/45 Sukhumvit Road',
        addressLine2: 'Khlong Toei, Khlong Toei',
        city: 'Bangkok',
        postcode: '10110'
      }));
      setDbdMeta({
        registerDate: '2020-03-15',
        juristicStatus: 'Active',
        juristicType: 'Limited Company',
        isMockFallback: true
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  if (isSubmitted) {
    return <div className="min-h-screen bg-[#1a2042] flex flex-col items-center justify-center p-4" data-id="element-11753">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-id="element-11754">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-11755">
            <CheckCircle2Icon size={32} className="text-green-600" data-id="element-11756" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-11757">
            Details submitted successfully
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-11758">
            Thank you! Your information has been sent to Siam Fresh Market Co.,
            Ltd. They will review and confirm your details.
          </p>
          <p className="text-sm text-gray-500" data-id="element-11759">You can close this page.</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#1a2042] py-12 px-4 sm:px-6 lg:px-8" data-id="element-11760">
      <div className="max-w-3xl mx-auto" data-id="element-11761">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-11762">
          <div className="flex items-center justify-center mb-6" data-id="element-11763">
            <img src="https://cdn.magicpatterns.com/uploads/epvnZYmMRqK1HNfS2q8PGf/rabbit_logo_sidebar.svg" alt="rabbit gateway" className="h-10" data-id="element-11764" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3" data-id="element-11765">
            Complete your supplier profile
          </h1>
          <p className="text-gray-400" data-id="element-11766">
            Siam Fresh Market Co., Ltd. has invited you to provide your business
            details and payout information.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-id="element-11767">
          <div className="px-8 py-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-11768">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-11769">
              Supplier Information
            </h2>

            {/* Supplier Type Segmented Control */}
            <div className="flex p-1 bg-gray-100 rounded-lg" data-id="element-11770">
              <button type="button" onClick={() => setSupplierType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-11771">
                <BuildingIcon size={16} className="mr-2" data-id="element-11772" />
                Company
              </button>
              <button type="button" onClick={() => setSupplierType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-11773">
                <UserIcon size={16} className="mr-2" data-id="element-11774" />
                Individual
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8" data-id="element-11775">
            {/* --- COMPANY FIELDS --- */}
            {supplierType === 'company' && <div className="space-y-6" data-id="element-11776">
                {/* DBD Lookup Section */}
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200" data-id="element-11777">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11778">
                    Tax ID / Registration Number *
                  </label>
                  <div className="flex gap-3" data-id="element-11779">
                    <input type="text" name="taxId" required maxLength={13} value={formData.taxId} onChange={handleChange} placeholder="13-digit registration number" className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white" data-id="element-11780" />
                    <button type="button" onClick={handleLookup} disabled={isLookingUp} className="flex items-center px-5 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] disabled:bg-[#F7941D]/70 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11781">
                      {isLookingUp ? <Loader2Icon size={18} className="animate-spin" data-id="element-11782" /> : <>
                          <SearchIcon size={18} className="mr-2" data-id="element-11783" />
                          Lookup
                        </>}
                    </button>
                  </div>

                  {lookupError && <p className="mt-2 text-sm text-red-600" data-id="element-11784">{lookupError}</p>}

                  {lookupSuccess && <div className="mt-4 space-y-2" data-id="element-11785">
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start" data-id="element-11786">
                        <CheckCircle2Icon size={20} className="text-green-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-11787" />
                        <div data-id="element-11788">
                          <p className="text-sm font-medium text-green-800" data-id="element-11789">
                            Company details fetched from Department of Business
                            Development (DBD)
                          </p>
                          <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-green-700" data-id="element-11790">
                            {dbdMeta.registerDate && <>
                                <span data-id="element-11791">Registered: {dbdMeta.registerDate}</span>
                                <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-11792"></span>
                              </>}
                            {dbdMeta.juristicStatus && <span className="font-semibold" data-id="element-11793">
                                {dbdMeta.juristicStatus}
                              </span>}
                            {dbdMeta.juristicType && <>
                                <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-11794"></span>
                                <span data-id="element-11795">{dbdMeta.juristicType}</span>
                              </>}
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-11796">
                  <div data-id="element-11797">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11798">
                      Company name (Local) *
                    </label>
                    <input type="text" name="companyNameTh" required value={formData.companyNameTh} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess ? 'bg-orange-50' : ''}`} data-id="element-11799" />
                  </div>
                  <div data-id="element-11800">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11801">
                      Company name (English)
                    </label>
                    <input type="text" name="companyNameEn" value={formData.companyNameEn} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess ? 'bg-orange-50' : ''}`} data-id="element-11802" />
                  </div>
                  <div data-id="element-11803">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11804">
                      Branch number *
                    </label>
                    <input type="text" name="branch" required value={formData.branch} onChange={handleChange} placeholder="00000" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11805" />
                  </div>
                  <div data-id="element-11806">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11807">
                      Contact person name
                    </label>
                    <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11808" />
                  </div>
                </div>

                <label className="flex items-center cursor-pointer group w-fit" data-id="element-11809">
                  <div className="relative flex items-center justify-center" data-id="element-11810">
                    <input type="checkbox" name="vatRegistered" checked={formData.vatRegistered} onChange={handleChange} className="peer sr-only" data-id="element-11811" />
                    <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#F7941D] peer-checked:border-[#F7941D] transition-colors" data-id="element-11812"></div>
                    <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-11813">
                      <polyline points="20 6 9 17 4 12" data-id="element-11814"></polyline>
                    </svg>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-11815">
                    VAT registered company
                  </span>
                </label>
              </div>}

            {/* --- INDIVIDUAL FIELDS --- */}
            {supplierType === 'individual' && <div className="space-y-6" data-id="element-11816">
                <div className="flex gap-6 mb-2" data-id="element-11817">
                  <label className="flex items-center cursor-pointer" data-id="element-11818">
                    <input type="radio" name="idType" value="citizen_id" checked={formData.idType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11819" />
                    <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-11820">
                      Thai Citizen ID
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer" data-id="element-11821">
                    <input type="radio" name="idType" value="passport" checked={formData.idType === 'passport'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11822" />
                    <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-11823">
                      Passport
                    </span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-11824">
                  <div className="md:col-span-2" data-id="element-11825">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11826">
                      {formData.idType === 'citizen_id' ? 'Citizen ID Number *' : 'Passport Number *'}
                    </label>
                    <input type="text" name="idNumber" required maxLength={formData.idType === 'citizen_id' ? 13 : undefined} value={formData.idNumber} onChange={handleChange} placeholder={formData.idType === 'citizen_id' ? '13-digit ID number' : 'Passport number'} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11827" />
                  </div>
                  <div data-id="element-11828">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11829">
                      Full name (Local)
                    </label>
                    <input type="text" name="fullNameTh" value={formData.fullNameTh} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11830" />
                  </div>
                  <div data-id="element-11831">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11832">
                      Full name *
                    </label>
                    <input type="text" name="fullNameEn" required value={formData.fullNameEn} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11833" />
                  </div>
                  <div className="md:col-span-2" data-id="element-11834">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11835">
                      Tax ID (if different from Citizen ID)
                    </label>
                    <input type="text" name="individualTaxId" value={formData.individualTaxId} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11836" />
                  </div>
                </div>
              </div>}

            {/* --- SHARED CONTACT FIELDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-gray-100" data-id="element-11837">
              <div data-id="element-11838">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11839">
                  Email address *
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="Enter email address" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11840" />
              </div>
              <div data-id="element-11841">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11842">
                  Phone number
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="e.g. 0812345678" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11843" />
              </div>
            </div>

            {/* Divider: Billing information */}
            <div className="relative py-2" data-id="element-11844">
              <div className="absolute inset-0 flex items-center" data-id="element-11845">
                <div className="w-full border-t border-gray-200" data-id="element-11846"></div>
              </div>
              <div className="relative flex justify-center" data-id="element-11847">
                <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11848">
                  Billing information
                </span>
              </div>
            </div>

            {/* Billing Info Fields */}
            <div className="space-y-5" data-id="element-11849">
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder="Address line 1" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11850" />
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder="Address line 2" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11851" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11852">
                <div data-id="element-11853">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-11854">
                    City / District
                  </label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g. Khlong Toei" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11855" />
                </div>
                <div data-id="element-11856">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-11857">
                    Province
                  </label>
                  <SearchableSelect name="province" options={THAI_PROVINCES} value={formData.province} onChange={val => setFormData(prev => ({
                  ...prev,
                  province: val
                }))} placeholder="Select province" data-id="element-11858" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11859">
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-11860">
                  Thailand
                </div>
                <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder="Postcode (5 digits)" className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-orange-50' : ''}`} data-id="element-11861" />
              </div>
            </div>

            {/* Divider: Payout method */}
            <div className="relative py-2" data-id="element-11862">
              <div className="absolute inset-0 flex items-center" data-id="element-11863">
                <div className="w-full border-t border-gray-200" data-id="element-11864"></div>
              </div>
              <div className="relative flex justify-center" data-id="element-11865">
                <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11866">
                  Payout method
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-500 -mt-4" data-id="element-11867">
              Choose how you would like to receive your funds.
            </p>

            {/* Payout Method Selection */}
            <div className="space-y-4" data-id="element-11868">
              {/* Bank Account Option */}
              <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'bank_account' ? 'border-[#F7941D] ring-1 ring-[#F7941D]' : 'border-gray-200'}`} data-id="element-11869">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-11870">
                  <input type="radio" name="payoutMethod" value="bank_account" checked={formData.payoutMethod === 'bank_account'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-11871" />
                  <div className="ml-3 flex items-center" data-id="element-11872">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3" data-id="element-11873">
                      <LandmarkIcon size={16} className="text-green-600" data-id="element-11874" />
                    </div>
                    <div data-id="element-11875">
                      <div className="text-sm font-medium text-gray-900" data-id="element-11876">
                        Bank Account
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-11877">
                        Direct transfer to a Thai bank account
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payoutMethod === 'bank_account' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-11878">
                    <div data-id="element-11879">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11880">
                        Bank name
                      </label>
                      <SearchableSelect name="bankName" options={THAI_BANKS} value={formData.bankName} onChange={val => setFormData(prev => ({
                    ...prev,
                    bankName: val
                  }))} placeholder="Select a bank" data-id="element-11881" />
                    </div>
                    <div data-id="element-11882">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11883">
                        Account number
                      </label>
                      <input type="text" name="accountNumber" maxLength={12} placeholder="10–12 digits" value={formData.accountNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11884" />
                    </div>
                    <div data-id="element-11885">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11886">
                        Account name
                      </label>
                      <input type="text" name="accountName" placeholder="Name on the bank account" value={formData.accountName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11887" />
                    </div>
                  </div>}
              </div>

              {/* PromptPay Option */}
              <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'promptpay' ? 'border-[#F7941D] ring-1 ring-[#F7941D]' : 'border-gray-200'}`} data-id="element-11888">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-11889">
                  <input type="radio" name="payoutMethod" value="promptpay" checked={formData.payoutMethod === 'promptpay'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D] mt-0.5" data-id="element-11890" />
                  <div className="ml-3 flex items-center" data-id="element-11891">
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center mr-3" data-id="element-11892">
                      <SmartphoneIcon size={16} className="text-purple-600" data-id="element-11893" />
                    </div>
                    <div data-id="element-11894">
                      <div className="text-sm font-medium text-gray-900" data-id="element-11895">
                        PromptPay
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-11896">
                        Fast transfer via Phone, Citizen ID, or Tax ID
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payoutMethod === 'promptpay' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-11897">
                    <div data-id="element-11898">
                      <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-11899">
                        PromptPay Type
                      </label>
                      <div className="flex flex-wrap gap-4" data-id="element-11900">
                        <label className="flex items-center" data-id="element-11901">
                          <input type="radio" name="promptPayType" value="phone" checked={formData.promptPayType === 'phone'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11902" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-11903">
                            Phone Number
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-11904">
                          <input type="radio" name="promptPayType" value="citizen_id" checked={formData.promptPayType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11905" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-11906">
                            Citizen ID
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-11907">
                          <input type="radio" name="promptPayType" value="tax_id" checked={formData.promptPayType === 'tax_id'} onChange={handleChange} className="w-4 h-4 text-[#F7941D] border-gray-300 focus:ring-[#F7941D]" data-id="element-11908" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-11909">
                            Tax ID
                          </span>
                        </label>
                      </div>
                    </div>
                    <div data-id="element-11910">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11911">
                        PromptPay ID
                      </label>
                      <input type="text" name="promptPayId" placeholder={formData.promptPayType === 'phone' ? '0XX-XXX-XXXX' : 'X-XXXX-XXXXX-XX-X'} value={formData.promptPayId} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11912" />
                    </div>
                    <div data-id="element-11913">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-11914">
                        Display name (for verification)
                      </label>
                      <input type="text" name="promptPayName" value={formData.promptPayName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-11915" />
                    </div>
                  </div>}
              </div>
            </div>

            {/* Form Actions */}
            <div className="pt-6 border-t border-gray-100" data-id="element-11916">
              <button type="submit" className="w-full px-6 py-3 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11917">
                Submit Details
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}